import { setSelectedTemplate } from "../../../features/template/template.slice";
import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import styled from "styled-components";
import regen from "../../../assets/inputGraphEditor/regenerate.svg";
import cont from "../../../assets/inputGraphEditor/continue.svg";
import ExportSection from "./ExportSection";
import FloorPlanSection from "./FloorPlanSection";
import GenerateGraphLayoutButton from "./GenerateGraphLayoutButton";
import { useDispatch, useSelector } from "react-redux";
import { fetchGraphData } from "../../../redux/api/api.graph";
import { AppDispatch, RootState } from "../../../redux/store";
import { useNavigate } from "react-router-dom";
import { setCleanUpRequired } from "../../../redux/features/appState";
import { showToast } from "../../../redux/features/toast/toast.slice";
import {
  setDimensioned,
  setFromDocuments,
  setGraphData,
} from "../../../redux/features/graph";
import { logEvent } from "../../../utils/analytics";
import {
  updateSelectedPlotWidth,
  updateSelectedPlotHeight,
} from "../../../redux/features/nodes";
import {
  fetchAndSaveSubscriptionsFromServer,
  getSubscribedProductNames,
} from "../../payment/SubscriptionInfo";
import applyPtpgGraph from "../services/applyPtpgGraph";

const CustomDiv = styled.div`
  .container {
    width: 100%;
    padding: 16px;
    display: flex;
    background: #FBE5C6;
    font-family: Poppins;
    gap: 16px;
    border-radius: 12px;
    border: 2px solid #1a1a1a;
  }

  .container2 {
    width: 100%;
    height: 100%;
    padding: 16px;
    display: flex;
    background: #FBE5C6;
    font-family: Poppins;
    gap: 16px;
    border-radius: 12px;
    border: 2px solid #1a1a1a;
    overflow: hidden;
  }

  .corridor,
  .plan {
    flex-direction: column;
  }

  .export,
  .btns {
    flex-direction: row;
  }

  .btns {
    padding: 0;
    background: none;
  }

  .plan,
  .export {
    padding: 16px 32px;
  }

  .btns {
    gap: 8px;
  }

  .icon {
    width: 36px;
  }

  .header {
    font-size: 14px !important;
    color: #FF9A56;
    font-weight: 500;
    &:hover {
      cursor: pointer;
    }
  }

  p {
    font-size: 12px;
    margin: 0;
    color: #879fba;
  }

  .setting {
    text-decoration: underline;
  }

  .arrow {
    width: 24px;
  }

  .line {
    width: 100%;
    height: 1px;
    background: #FBE5C6;
  }

  .head {
    font-size: 18px;
    line-height: 20px;
    font-weight: 500 !important;
    color: #FF9A56;
    margin-top: 5%;
  }

  .MuiInputBase-root {
    padding: 0;
  }

  .MuiSelect-select {
    padding: 8px;
  }
`;

const Sidebar = ({ setLeftSidebarOpen }: any) => {
  const navigate = useNavigate();
  const nodes = useSelector((state: RootState) => state.nodesState.nodes);
  const links = useSelector((state: RootState) => state.nodesState.links);
  const plotWidth = useSelector(
    (state: RootState) => state.nodesState.plotWidth,
  );
  const plotHeight = useSelector(
    (state: RootState) => state.nodesState.plotHeight,
  );
  const selectedTemplate = useSelector(
    (state: RootState) => state.templateState.selectedTemplate
  );
  const floorPlans = useSelector((state: RootState) => state.graph.floorPlans);
  const index = useSelector((state: RootState) => state.graph.index);
  const selectedFloorplan = useSelector(
    (state: RootState) => state.graph.selectedFloorPlan,
  );
  const unit = useSelector(
    (state: RootState) => state.outputEditorButtonState.unit,
  );
  const shape = useSelector((state: RootState) => state.shapeState.shape);
  const userName = useSelector((state: RootState) => state.auth.user?.username); // Updated
  const dispatch = useDispatch<AppDispatch>();

  const [isDimensioned, setIsDimensioned] = useState(false);
  const [isErr, setIsErr] = useState(false);
  const [WEB_FRONTEND_ACCESS, setWebFrontendAccess] = useState(false);

  const formattedNodes = (
    selectedTemplate === "housing"
      ? nodes.map((node: any) => ({
          ...node,
          roomtype: node.roomtype ?? "Others",
        }))
      : nodes
  ).map((node: any) => {
    if (isDimensioned) {
      return {
        ...node,
        width: {
          min: node.width?.min ?? 3,
          max: node.width?.min ?? 3,
        },
        height: {
          min: node.height?.min ?? 3,
          max: node.height?.min ?? 3,
        },
        ratio: {
          min: node.ratio?.min ?? 3,
          max: node.ratio?.min ?? 3,
        },
      };
    }
    return node;
  });

  useEffect(() => {
    const fetchSubscriptions = async () => {
      let subscribedProducts = getSubscribedProductNames();
      if (!subscribedProducts || subscribedProducts.length === 0) {
        try {
          subscribedProducts = await fetchAndSaveSubscriptionsFromServer();
        } catch (err) {
          subscribedProducts = [];
        }
      }
      setWebFrontendAccess(subscribedProducts.includes("Web Frontend"));
      setIsDimensioned(subscribedProducts.includes("Web Frontend"));
    };
    fetchSubscriptions();
  }, []);

  useEffect(() => {
    dispatch(setDimensioned(isDimensioned));
  }, [isDimensioned, dispatch]);

  useEffect(() => {
    if (isDimensioned) {
      emitDataRectangular();
    } else {
      emitDataIrregular();
    }
  }, [isDimensioned]);

  const handleToggle = (check: boolean) => {
    if (check) {
      setIsDimensioned(true);
      setLeftSidebarOpen(true);
      return;
    }
    setIsDimensioned(false);
    setLeftSidebarOpen(false);
  };

  let graphData = {
    rectangular: false,
    corridor: false,
    dimensioned: isDimensioned,
    dimensionedCirculation: isDimensioned,
    non_adj: true,
    minDimEnabled: isDimensioned,
    removeAddCirculation: false,
    publicEnabled: false,
    normalizeConst: true,
    limit: 10,
    corridorThickness: 0.5,
    starting_from: 0,
    count: 10,
    nodes: formattedNodes,
    edges: links,
    plot_width: plotWidth,
    plot_height: plotHeight,
  };

  let graphData2 = {
    rectangular: true,
    corridor: false,
    dimensioned: isDimensioned,
    non_adj: true,
    dimensionedCirculation: isDimensioned,
    minDimEnabled: isDimensioned,
    removeAddCirculation: false,
    publicEnabled: false,
    normalizeConst: true,
    limit: 10,
    corridorThickness: 0.5,
    starting_from: 0,
    count: 10,
    nodes: formattedNodes,
    edges: links,
    plot_width: plotWidth,
    plot_height: plotHeight,
  };

  const handleClick = () => {
    const graphDataToUse = isDimensioned ? graphData2 : graphData;
    // dispatch(updatePlotWidth(0));
    // dispatch(updatePlotHeight(0));

    // Log the event with custom data
    logEvent({
      category: "Button",
      action: "Click",
      label: "Generate Floorplans",
      customData: {
        isDimensioned: isDimensioned,
        graphData: JSON.stringify(graphDataToUse),
        username: userName,
        shape: shape,
        timestamp: new Date().toISOString(),
      },
    });

    if (isDimensioned) {
      emitDataRectangular();
      applyPtpgGraph({ nodes, links, plotWidth, plotHeight, isDimensioned, dispatch }).catch(
        (err) => console.error("applyPtpgGraph error", err),
      );
      return;
    }

    switch (shape) {
      case "irregular":
        emitDataIrregular();
        break;
      case "rectangular":
        emitDataRectangular();
        dispatch(updateSelectedPlotWidth(plotWidth));
        dispatch(updateSelectedPlotHeight(plotHeight));
        break;

      default:
        break;
    }
    applyPtpgGraph({ nodes, links, plotWidth, plotHeight, isDimensioned, dispatch }).catch(
      (err) => console.error("applyPtpgGraph error", err),
    );
    dispatch(setCleanUpRequired(true));
  };

  const handleEditFloorPlan = () => {
    localStorage.removeItem("fetched_graph-data_un-normalized_edges");
    localStorage.removeItem("fetched_graph-data_un-normalized_nodes");
    if (selectedFloorplan.length === 0) {
      dispatch(
        showToast({
          message: "Please select a floorplan!",
          type: "error",
        }),
      );
      return;
    }
    const graphDataToUse = isDimensioned ? graphData2 : graphData;
    dispatch(setGraphData(graphDataToUse));

    // Log the event with custom data
    logEvent({
      category: "Button",
      action: "Click",
      label: "Edit Floorplans",
      customData: {
        isDimensioned: isDimensioned,
        graphData: JSON.stringify(graphDataToUse),
        index: index?.toString(),
        floorPlans: JSON.stringify(floorPlans),
        username: userName,
        shape: shape,
        timestamp: new Date().toISOString(),
      },
    });
    console.log({
      category: "Button",
      action: "Click",
      label: "Edit Floorplans",
      customData: {
        isDimensioned: isDimensioned,
        graphData: JSON.stringify(graphDataToUse),
        index: index?.toString(),
        floorPlans: JSON.stringify(floorPlans),
        username: userName,
        shape: shape,
        timestamp: new Date().toISOString(),
      },
    });
    dispatch(setCleanUpRequired(true));
    dispatch(setFromDocuments(false));
    navigate("/outputeditor", {
      state: {
        originPath: "/inputeditor",
      },
      /* state: {
        graphData: graphDataToUse,
        plotWidth: plotWidth,
        plotHeight: plotHeight,
      }, */
    });
  };

  const emitDataIrregular = () => {
    dispatch(fetchGraphData({ graphData, type: "irregular" }));
    dispatch(updateSelectedPlotWidth(plotWidth));
    dispatch(updateSelectedPlotHeight(plotHeight));
  };

  const emitDataRectangular = () => {
    dispatch(
      fetchGraphData({ graphData: graphData2, type: "door_connectivity" }),
    );
    dispatch(updateSelectedPlotWidth(plotWidth));
    dispatch(updateSelectedPlotHeight(plotHeight));
  };

  return (
    <>
      <Box
        sx={{
          width: "25vw",
          display: "flex",
          /* justifyContent: "space-between", */
          flexDirection: "column",
          gap: "8px",
          height: "100vh",
          overflowY: "scroll",
          overflowX: "hidden",
          padding: "11px",
          "-ms-overflow-style": "none" /* IE and Edge */,
          scrollbarWidth: "none" /* Firefox */,
        }}
      >
        <Box
          sx={{
            flex: 1.3,
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: "#FBE5C6",
            alignItems: "center",
            border: "none",
            borderRadius: "8px",
            minHeight: "10%",
            height: "auto",
          }}
        >
          <button
            onClick={() => handleToggle(true)}
            style={{
              width: "48%",
              padding: "10px 8px",
              marginLeft: "6px",
              fontFamily: "Poppins",
              fontSize: "14.5px",
              fontWeight: "500",
              lineHeight: "20px",
              backgroundColor: isDimensioned ? "#FF9A56" : "#FBE5C6",
              outline: "none",
              border: "none",
              borderRadius: "6px",
              color: isDimensioned ? "#FBE5C6" : "#111111",
              cursor: "pointer",
            }}
          >
            Dimensioned Floorplans
          </button>
          <button
            onClick={() => handleToggle(false)}
            style={{
              width: "48%",
              padding: "10px 8px",
              marginRight: "6px",
              fontFamily: "Poppins",
              fontSize: "14.5px",
              fontWeight: "500",
              lineHeight: "20px",
              backgroundColor: !isDimensioned ? "#FF9A56" : "#FBE5C6",
              outline: "none",
              border: "none",
              borderRadius: "6px",
              color: !isDimensioned ? "#FBE5C6" : "#111111",
              cursor: "pointer",
            }}
          >
            Dimensionless Floorplans
          </button>
        </Box>
        {/* <CorridorSection /> */}{
          <FloorPlanSection err={isErr} isDimensioned={isDimensioned} />

        }       {/* Buttons */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "5px",
          }}
        >
          {/* <GenerateGraphLayoutButton /> */}

          <CustomDiv>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ width: "53%" }}>
                <button
                  data-tour="generate-floorplans"
                  style={{
                    backgroundColor: "#FBE5C6",
                    color: "#1a1a1a",
                    border: "2px solid #1a1a1a",
                    borderRadius: "12px",
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "normal",
                    width: "100%",
                    padding: "10px 8px",
                    height: "100%",
                    cursor: "pointer",
                    transition: "all 0.1s ease",
                  }}
                  onClick={handleClick}
                >
                  Generate Floorplans
                  <img src={regen} alt="" />
                </button>
              </div>
              <div style={{ width: "45%" }}>
                <button
                  data-tour="edit-floorplan"
                  disabled={false}
                  style={{
                    backgroundColor: "#FF9A56",
                    color: "#1a1a1a",
                    border: "2px solid #1a1a1a",
                    borderRadius: "12px",
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "normal",
                    padding: "10px 8px",
                    width: "100%",
                    height: "100%",
                    cursor: "pointer",
                    transition: "all 0.1s ease",
                  }}
                  onClick={handleEditFloorPlan}
                >
                  Edit Floorplan
                  <img src={cont} alt="" />
                </button>
              </div>
            </div>
          </CustomDiv>
          <ExportSection isDimensioned={isDimensioned} />
        </Box>
      </Box>
    </>
  );
};

export { CustomDiv };
export default Sidebar;
